import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { useFundLayoutContext } from '@/components/fund-detail/useFundLayoutContext';
import { CustomAllocationCheckbox } from '@/components/funds/lp-allocation/CustomAllocationCheckbox';
import { LpAllocationCard } from '@/components/funds/lp-allocation/LpAllocationCard';
import { useLpAllocationEditor } from '@/components/funds/lp-allocation/useLpAllocationEditor';
import { ConversionFundingChain } from '@/components/transactions/ConversionFundingChain';
import { TransactionActivityLog } from '@/components/transactions/TransactionActivityLog';
import { PageSkeleton } from '@/components/ui/DataLoader';
import {
  useConversionFundingChain,
  useDeleteCurrencyConversion,
  useDiscardFxPeriodEnd,
} from '@/hooks/react-query/useCurrencyConversions';
import {
  useFundTransaction,
  useFundTransactionActivity,
  useFundTransactionBankEntries,
  useFundTransactionReconciliation,
} from '@/hooks/react-query/useFundTransactions';
import type {
  FundTransactionDetail,
  FundTransactionType,
  SlotState,
} from '@/types/fund-transactions';

import {
  FundBankEntryDialog,
  type BankDialogState,
} from './FundBankEntryDialog';
import { FundJournalEntriesDialog } from './FundJournalEntriesDialog';
import { MatchPontoTransactionDialog } from './MatchPontoTransactionDialog';
import {
  FundDocumentBlock,
  FundTransactionVerificationCard,
  LegalDocBlock,
} from './FundTransactionVerificationCard';
import { ManualApproveDialog } from './ManualApproveDialog';
import { TransactionExtras } from './TransactionExtras';
import { Field } from './transactionDetails';
import { renderDetailsCard } from './transactionDetailsCardFactory';

export { FundTransactionParentBreadcrumb } from './FundTransactionParentBreadcrumb';

const RECONCILABLE_PROJECTION_TYPES = new Set<FundTransactionType>([
  'ASSET_SALE',
  'INVESTMENT_PURCHASE',
  'FINANCING_ROUND',
  'CONVERTIBLE_INVESTMENT',
  'CONVERTIBLE_CONVERSION',
  'CONVERTIBLE_REPAYMENT',
]);

const INVESTMENT_TYPES = new Set<FundTransactionType>([
  'FINANCING_ROUND',
  'INVESTMENT_PURCHASE',
  'CONVERTIBLE_INVESTMENT',
]);

function isCaptableProjection(transaction?: FundTransactionDetail) {
  return (
    transaction?.captable_transaction_id != null ||
    transaction?.captable_ledger_transaction_id != null
  );
}

function isReconcilableProjectionType(transaction?: FundTransactionDetail) {
  return (
    !!transaction &&
    RECONCILABLE_PROJECTION_TYPES.has(transaction.transaction_type)
  );
}

export function FundTransactionViewPage() {
  const { fundId = '', transactionId = '' } = useParams<{
    fundId: string;
    transactionId: string;
  }>();
  const { fund } = useFundLayoutContext();
  const navigate = useNavigate();
  const discardPeriodEnd = useDiscardFxPeriodEnd(fundId);
  const deleteConversion = useDeleteCurrencyConversion(fundId);

  const { data: transaction, isLoading: txLoading } = useFundTransaction(
    fundId,
    transactionId
  );
  const canonicalTransactionId = transaction?.id ?? transactionId;
  const { data: activity, isLoading: activityLoading } =
    useFundTransactionActivity(fundId, canonicalTransactionId);

  const isProjection = isCaptableProjection(transaction);
  const hideFundSideWorkflow =
    isProjection && !isReconcilableProjectionType(transaction);
  const isRecognition =
    transaction?.transaction_type === 'FUND_COST_RECOGNITION' ||
    transaction?.transaction_type === 'MANAGEMENT_FEE_RECOGNITION';

  const drawdownId = transaction?.drawdown?.drawdown_id;

  const isConversion = transaction?.transaction_type === 'CURRENCY_CONVERSION';
  const { data: fundingChain } = useConversionFundingChain(
    isConversion ? fundId : undefined,
    isConversion ? canonicalTransactionId : undefined
  );

  const { data: reconciliation, isLoading: reconLoading } =
    useFundTransactionReconciliation(
      fundId,
      canonicalTransactionId,
      !!transaction && !hideFundSideWorkflow && !drawdownId
    );
  const { data: bankEntries } = useFundTransactionBankEntries(
    fundId,
    canonicalTransactionId
  );

  const isInvestment =
    !!transaction &&
    !hideFundSideWorkflow &&
    INVESTMENT_TYPES.has(transaction.transaction_type);
  const isDealByDealInvestment =
    isInvestment && fund.lp_allocation_mode === 'DEAL_BY_DEAL';
  // A custom split approved before the fund left deal-by-deal stays on show.
  const lpAllocation = useLpAllocationEditor(
    fundId,
    canonicalTransactionId,
    isInvestment
  );

  const [bankDialog, setBankDialog] = useState<BankDialogState>(null);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [journalEntriesDialogOpen, setJournalEntriesDialogOpen] =
    useState(false);

  if (transaction && transactionId !== transaction.id) {
    return (
      <Navigate
        to={`/app/funds/${fundId}/fund-transactions/${transaction.id}`}
        replace
      />
    );
  }

  if (drawdownId) {
    return (
      <Navigate
        to={`/app/funds/${fundId}/drawdowns/${drawdownId}/preview`}
        replace
      />
    );
  }

  if (txLoading || (!hideFundSideWorkflow && reconLoading)) {
    return <PageSkeleton />;
  }

  if (!transaction) {
    return (
      <p className="p-6 text-sm text-muted-foreground">
        Transaction not found.
      </p>
    );
  }

  const isOpenForBankEntries =
    transaction.status === 'DRAFT' || transaction.status === 'ISSUED';
  const hasJournalEntries = transaction.status !== 'DRAFT';
  const onViewJournalEntries = hasJournalEntries
    ? () => setJournalEntriesDialogOpen(true)
    : undefined;
  const renderDocument = (slot: SlotState) =>
    isProjection ? (
      <LegalDocBlock
        slot={slot}
        companyId={
          transaction.asset_sale?.portfolio_company_id ??
          transaction.financing_round?.portfolio_company_id ??
          transaction.convertible?.portfolio_company_id
        }
        assetSaleId={transaction.captable_transaction_id}
        ledgerTransactionId={transaction.captable_ledger_transaction_id}
      />
    ) : (
      <FundDocumentBlock
        slot={slot}
        fundId={fundId}
        transactionId={canonicalTransactionId}
        canEdit={isOpenForBankEntries}
      />
    );

  // Owned here rather than in the card: the row is gone once this lands, so
  // whoever runs it has to leave the page it was showing.
  const onDiscardPeriodEnd = async () => {
    await discardPeriodEnd.mutateAsync(canonicalTransactionId);
    navigate(`/app/funds/${fundId}?tab=transactions&section=all`);
  };

  const onDeleteConversion = async () => {
    await deleteConversion.mutateAsync(canonicalTransactionId);
    navigate(`/app/funds/${fundId}?tab=transactions&section=all`);
  };

  return (
    <div className="space-y-8">
      {renderDetailsCard({
        transaction,
        fundId,
        fundName: fund.name,
        fundCurrency: fund.base_currency,
        reconciliation: hideFundSideWorkflow ? undefined : reconciliation,
        onViewJournalEntries,
        onDiscardPeriodEnd,
        onDeleteConversion,
        investmentDescriptionAside: isDealByDealInvestment ? (
          <Field label="LP allocation">
            <CustomAllocationCheckbox editor={lpAllocation} />
          </Field>
        ) : null,
      })}

      {!hideFundSideWorkflow && (
        <TransactionExtras fundId={fundId} transaction={transaction} />
      )}

      {!hideFundSideWorkflow && !isRecognition && reconciliation && (
        <FundTransactionVerificationCard
          fundId={fundId}
          transactionId={canonicalTransactionId}
          state={reconciliation}
          canEdit={isOpenForBankEntries}
          draftCreatedAt={transaction.created_at}
          bankEntries={bankEntries ?? []}
          transactionAmount={transaction.amount}
          transactionCurrency={transaction.currency}
          onAddBank={() => setBankDialog({ mode: 'add' })}
          onEditBank={(entry) => setBankDialog({ mode: 'edit', entry })}
          onMatchBankLine={() => setMatchDialogOpen(true)}
          onManualApprove={() => setManualDialogOpen(true)}
          renderDocument={renderDocument}
          bankLines={
            fundingChain ? (
              <ConversionFundingChain fundId={fundId} chain={fundingChain} />
            ) : undefined
          }
        />
      )}

      {isInvestment && (
        <LpAllocationCard
          editor={lpAllocation}
          currency={transaction.currency}
        />
      )}

      <TransactionActivityLog entries={activity} isLoading={activityLoading} />

      <FundBankEntryDialog
        state={bankDialog}
        onClose={() => setBankDialog(null)}
        fundId={fundId}
        transactionId={canonicalTransactionId}
        transactionAmount={transaction.amount}
        transactionCurrency={transaction.currency}
        transactionCurrencyId={transaction.currency_id}
        transactionDate={transaction.transaction_date}
      />

      {!hideFundSideWorkflow && !isRecognition && (
        <MatchPontoTransactionDialog
          open={matchDialogOpen}
          onOpenChange={setMatchDialogOpen}
          fundId={fundId}
          transactionId={canonicalTransactionId}
        />
      )}

      {!hideFundSideWorkflow && !isRecognition && (
        <ManualApproveDialog
          open={manualDialogOpen}
          onOpenChange={setManualDialogOpen}
          fundId={fundId}
          transactionId={canonicalTransactionId}
        />
      )}

      <FundJournalEntriesDialog
        open={journalEntriesDialogOpen}
        onOpenChange={setJournalEntriesDialogOpen}
        fundId={fundId}
        transactionId={canonicalTransactionId}
      />
    </div>
  );
}
